<template>
	<SimpleModalComponent
		heading="Add asset"
		@close-modal="emit( 'close-modal' )"
	>
		<template #content>
			<p class="mb-16">
				Please name your asset e.g. EXT13_Pool Deck
			</p>

			<div class="form__set">
				<label
					for="new_tag"
					class="form__label form__label--large form__label--emphasised"
				>
					Code
				</label>
				<span class="form__label form__label--emphasised">
					Select the code prefix and enter a number (letters allowed if required e.g. 13A)
				</span>

				<div class="flex flex--gap-medium">
					<div class="form__icon form__icon--down">
						<select
							id="code-prefix"
							v-model="code_prefix"
							class="form__select"
							:class="{ 'form__select--error': errors.code_prefix }"
						>
							<option
								value=""
								disabled
								selected
							>
								Select
							</option>
							<option
								v-for="prefix in possible_code_prefixes"
								:key="`prefix-${ prefix }`"
								:value="prefix"
								:disabled="loading"
							>
								{{ prefix }}
							</option>
						</select>
					</div>

					<div
						class="form__set form__set--no-margin"
						:class="{ 'form__set--error': errors.code_number }"
					>
						<input
							id="code-number"
							:value="code_number"
							class="form__input"
							type="text"
							name="title"
							placeholder=""
							:disabled="loading"
							@input="validateCodeInput"
						>
					</div>
				</div>
			</div>

			<div
				class="form__set"
				:class="{ 'form__set--error': errors.title }"
			>
				<label
					for="new_tag"
					class="form__label form__label--large form__label--emphasised"
				>
					Title
				</label>
				<span class="form__label form__label--emphasised">
					Enter a descriptive title for the asset. Letters and numbers only e.g. Apartment 2 Dining Room
				</span>
				<input
					id="title"
					:value="title"
					class="form__input form__input--stretch"
					type="text"
					name="title"
					placeholder=""
					:disabled="loading"
					@input="validateTitleInput"
				>
			</div>

			<p
				v-if="error_message"
				class="error-message"
			>
				{{ error_message }}
			</p>
		</template>

		<template #buttons>
			<button
				class="button"
				:disabled="loading"
				@click="closeModal"
			>
				Cancel
			</button>
			<button
				class="button button--fill"
				:class="{ 'button--loading' : loading }"
				:disabled="loading"
				@click="createNewAsset"
			>
				Add
			</button>
		</template>
	</SimpleModalComponent>
</template>

<script setup>
import SimpleModalComponent from './SimpleModalComponent.vue';

import { ref, defineProps, defineEmits } from 'vue';
import { useDataStore } from '../../stores/data';

const props = defineProps( {
	project: {
		required: true,
		type: Object
	},
	mediaTypeId: {
		required: true,
		type: Number
	},
} );

const emit = defineEmits( ['asset-added', 'close-modal'] );

const data_store = useDataStore();
const { createAsset } = data_store;

const loading = ref( false );
const possible_code_prefixes = ref( [ 'EXT', 'INT', 'VIG', 'CIN', 'ANI', 'SHOT' ] );
const code_prefix = ref( '' );
const code_number = ref( '' );
const title = ref( '' );
const errors = ref( {} );
const error_message = ref( null );

const closeModal = () => { // eslint-disable-line snakecasejs/snakecasejs
	emit( 'close-modal' );
};

const validateCodeInput = ( e ) => { // eslint-disable-line snakecasejs/snakecasejs
	const prev_value = code_number.value;
	code_number.value = null;

	const regex = /^[A-Z0-9]*$/;
	const input = e.target.value.toUpperCase();

	if ( regex.test( input ) ) {
		code_number.value = input;
	} else {
		code_number.value = prev_value;
	}
};

const validateTitleInput = ( e ) => { // eslint-disable-line snakecasejs/snakecasejs
	const prev_value = title.value;
	title.value = null;

	const regex = /^[a-zA-Z0-9\ \-]*$/;
	const input = e.target.value;

	if ( regex.test( input ) ) {
		title.value = input;
	} else {
		title.value = prev_value;
	}
};

const createAssetTitle = () => { // eslint-disable-line snakecasejs/snakecasejs
	return code_prefix.value + code_number.value + '_' + title.value;
};

const createNewAsset = async() => { // eslint-disable-line snakecasejs/snakecasejs
	errors.value = {};
	error_message.value = null;
	loading.value = true;

	if ( !code_prefix.value ) {
		errors.value.code_prefix = 'Please choose a code prefix';
	}
	if ( !code_number.value ) {
		errors.value.code_number = 'Please enter a code';
	}
	if ( !title.value ) {
		errors.value.title = 'Please enter a title';
	}

	if ( Object.keys( errors.value ).length > 0 ) {
		error_message.value = 'Please make sure all fields are filled in';
		loading.value = false;
		return;
	}

	try {
		const new_asset = await createAsset( {
			project: props.project,
			asset_title: createAssetTitle(),
			media_type_id: props.mediaTypeId
		} );
		if ( new_asset ) {
			emit( 'asset-added', {
				new_asset,
				media_type_id: props.mediaTypeId
			} );
		}
	} catch ( error ) {
		error_message.value = error;
		loading.value = false;
	}
};
</script>